import { ChangeDetectionStrategy, Component, type OnInit } from '@angular/core';
import {
  FormControl,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { ButtonModule } from '@fino-ui/button';
import { SubmittableForm } from '@fino-ui/forms';
import { FinoInputModule } from '@fino-ui/input';
import { BaseModalComponent } from '@fino-ui/modal';
import { TranslationModule } from '@fino-ui/translation';
import { PleaseBePatientComponent } from '../../../../components/please-be-patient/please-be-patient.component';
import type { CustomOrIntba } from '../../kwh.model';

@Component({
  selector: 'app-letter-template',
  templateUrl: './letter-template.component.html',
  styleUrl: './letter-template.component.sass',
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    TranslationModule,
    PleaseBePatientComponent,
    FormsModule,
    ReactiveFormsModule,
    FinoInputModule,
    ButtonModule,
  ],
})
export class LetterTemplateComponent
  extends BaseModalComponent<
    {
      faxNumber: string;
      phoneNumber: string;
      department: string;

      city: CustomOrIntba;
      address: CustomOrIntba;
      postcode: CustomOrIntba;
      displayName: CustomOrIntba;
    },
    {}
  >
  implements OnInit
{
  constructor() {
    super();
    Object.assign(this, this.data ?? {});
  }

  faxNumber: string;
  phoneNumber: string;
  department: string;

  city: CustomOrIntba;
  address: CustomOrIntba;
  postcode: CustomOrIntba;
  displayName: CustomOrIntba;

  form = new SubmittableForm({
    faxNumber: new FormControl(''),
    phoneNumber: new FormControl(''),
    city: new FormControl(''),
    address: new FormControl(''),
    postcode: new FormControl(''),
    displayName: new FormControl(''),
    department: new FormControl(''),

    displayNameCustomEnabled: new FormControl(false),
    customAddressEnabled: new FormControl(false),
  });

  ngOnInit(): void {
    this.bindWatchers();

    const hasCustomAddress =
      this.city.customValue ||
      this.address.customValue ||
      this.postcode.customValue;

    this.form.setValue({
      faxNumber: this.faxNumber,
      phoneNumber: this.phoneNumber,
      department: this.department,

      city: this.city.customValue || this.city.intbaValue,
      address: this.address.customValue || this.address.intbaValue,
      postcode: this.postcode.customValue || this.postcode.intbaValue,
      displayName: this.displayName.customValue || this.displayName.intbaValue,

      customAddressEnabled: !!hasCustomAddress,
      displayNameCustomEnabled: !!this.displayName.customValue,
    });
  }

  bindWatchers() {
    this.form.controls.customAddressEnabled.valueChanges.subscribe((v) => {
      const cityControl = this.form.controls.city;
      const addressControl = this.form.controls.address;
      const postCodeControl = this.form.controls.postcode;

      [cityControl, addressControl, postCodeControl].forEach((c) => {
        if (v) {
          c.enable();
          c.setValue('');
          c.addValidators([Validators.required]);
          // addValidators does not revalidate, so without this the now-empty
          // control stays VALID and the form would accept an empty address.
          c.updateValueAndValidity();
        } else {
          c.removeValidators([Validators.required]);
          c.disable();
        }
      });
    });

    this.form.controls.displayNameCustomEnabled.valueChanges.subscribe((v) => {
      const control = this.form.controls.displayName;
      v ? control.enable() : control.disable();
    });
  }

  abort() {
    this.close();
  }

  submit() {
    const values = this.form.value;
    const {
      faxNumber,
      phoneNumber,
      department,
      city,
      address,
      postcode,
      displayName,
      customAddressEnabled,
      displayNameCustomEnabled,
    } = values;

    this.result = {
      faxNumber,
      phoneNumber,
      department,
      city: customAddressEnabled ? city : '',
      address: customAddressEnabled ? address : '',
      postcode: customAddressEnabled ? postcode : '',
      displayName: displayNameCustomEnabled ? displayName : '',
    };

    this.close();
  }
}
